/**
 * Buyer-side diligence checklist. Pure function over the deal package, diligence
 * JSON, and the existing asset-workflow inventory (Data Room). No document bytes.
 * Seller decline curves are the seller forecast case, whatever file the seller sent.
 *
 * v2 reads the checklist as three instruments: pre-offer data requests, pre-closing
 * tasks keyed to the closing date, post-closing tasks keyed to the effective date.
 * Rows carry an owner, a due date, typed capture fields, cross-phase feeds,
 * jurisdiction labels and buyer-profile names.
 */
import catalogJson from '../../config/diligence/buyer-side-diligence-checklist.v2.json' with { type: 'json' };
import buyerDefault from '../../config/diligence/buyer-profile.default.json' with { type: 'json' };
import { resolvedDocType } from './docTypes.js';

export const CATALOG = catalogJson;
export const CHECKLIST_NAME = 'buyer-side diligence checklist';
export const STATUSES = ['Complete', 'Partial', 'Missing', 'N/A'];
export const OWNERS = catalogJson.owners || ['Accounting', 'Operations', 'Land', 'Legal', 'Engineering'];
export const PHASE_LABEL = { pre_offer: 'Pre-offer', pre_closing: 'Pre-closing', post_closing: 'Post-closing' };
export const CATEGORY_LABEL = {
  financial: 'Financial / Accounting',
  operations: 'Operations',
  land_legal: 'Land / Legal / Engineering',
};
const PHASE_ORDER = { pre_offer: 0, pre_closing: 1, post_closing: 2 };
const STALE_MONTHS = 3;

export function resolveStage(overrides, stage) {
  if (stage === 'pre_offer' || stage === 'pre_closing' || stage === 'post_closing') return stage;
  const row = (overrides || []).find((o) => (o.itemId || o.item_id) === '__stage__');
  const note = row?.note;
  if (note === 'pre_closing' || note === 'post_closing' || note === 'pre_offer') return note;
  return 'pre_offer';
}

function phaseReached(itemPhase, stage) {
  return (PHASE_ORDER[itemPhase] ?? 0) <= (PHASE_ORDER[stage] ?? 0);
}

function isoDay(v) {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(v || ''));
  return m ? m[1] : null;
}

function addDays(iso, days) {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
}

function ymIndex(v) {
  const m = /^(\d{4})-(\d{2})/.exec(String(v || ''));
  return m ? Number(m[1]) * 12 + Number(m[2]) - 1 : null;
}

function ymLabel(index) {
  if (index == null) return '';
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  return `${y}-${String(m).padStart(2, '0')}`;
}

function latestYm(list, pick) {
  let best = null;
  for (const x of list || []) {
    const i = ymIndex(pick(x));
    if (i != null && (best == null || i > best)) best = i;
  }
  return best;
}

/** Months between the newest data point and the effective date (or today). >STALE_MONTHS is stale. */
function staleness(ctx, latest) {
  const anchor = ymIndex(ctx.dates.effectiveDate) ?? ymIndex(ctx.dates.today);
  if (anchor == null || latest == null) return null;
  return anchor - latest;
}

function staleNote(ctx, latest, what) {
  const gap = staleness(ctx, latest);
  if (gap == null || gap <= STALE_MONTHS) return '';
  const anchor = ctx.dates.effectiveDate ? 'the effective date' : 'today';
  return `${what} ends ${ymLabel(latest)}, ${gap} months before ${anchor}.`;
}

function historyDate(entry) {
  if (Array.isArray(entry)) return entry[0];
  if (entry && typeof entry === 'object') return entry.date || entry.month || entry.ym;
  return entry;
}

function interpolate(text, buyer) {
  return String(text || '').replace(/\{(\w+)\}/g, (m, key) => (buyer && buyer[key] ? String(buyer[key]) : m));
}

function labelFor(item, deal) {
  const labels = item.labels;
  const states = deal.states || [];
  if (!labels) return item.item;
  if (states.length === 1 && labels[states[0]]) return labels[states[0]];
  const base = labels.default || item.item;
  return states.length ? `${base} (${states.join(', ')})` : base;
}

function applicability(item, deal) {
  if (item.operatedOnly && deal.operated === false) {
    return { applicable: false, reason: 'Not applicable: non-operated purchase. The operator keeps this.' };
  }
  return { applicable: true, reason: '' };
}

function dueDateFor(item, dates) {
  if (!item.due) return null;
  const anchor = item.due.anchor === 'effective' ? dates.effectiveDate : dates.closingDate;
  if (!anchor) return null;
  return addDays(anchor, item.due.offsetDays);
}

function docsOf(ctx, types) {
  const want = new Set(types || []);
  if (!want.size) return [];
  return ctx.docs.filter((d) => d.docType && want.has(d.docType));
}

function docEv(docs) {
  return docs.slice(0, 12).map((d) => ({
    kind: 'doc',
    docId: d.docId || null,
    docType: d.docType || null,
    label: d.title || d.docId || d.docType,
  }));
}

function fieldEv(label, ref) {
  return { kind: 'field', label, ref: ref || null };
}

function done(status, evidence = [], detail = '') {
  return { status, evidence, detail };
}

function losMonths(ctx) {
  return ctx.pkg?.cashflow?.losMonths || ctx.pkg?.losMonths || [];
}

function contracts(ctx) {
  return ctx.diligence?.midstreamAssignability?.contracts || [];
}

function masterList(ctx) {
  return ctx.wells?.masterWells || ctx.pkg?.masterWells || ctx.diligence?.masterWells || [];
}

function statusCounts(ctx) {
  return ctx.wells?.statusCounts || ctx.pkg?.statusCounts || ctx.diligence?.statusCounts || null;
}

function acreageUnits(ctx) {
  return ctx.land?.units || ctx.pkg?.acreageUnits || ctx.diligence?.acreageUnits || ctx.wells?.acreageUnits || [];
}

function hasInterest(w) {
  return w != null && Number.isFinite(Number(w.wi)) && Number.isFinite(Number(w.nri));
}

function modeledWells(ctx) {
  const assets = ctx.pkg?.assets || [];
  if (assets.length && assets.some((a) => a.wi != null || a.nri != null)) return assets;
  const wfWells = ctx.wf?.wells || [];
  if (wfWells.length) return wfWells;
  const master = masterList(ctx);
  const modeled = master.filter((w) => w.modeled !== false);
  return modeled.length ? modeled : master;
}

function isPdp(w) {
  const s = `${w?.status || ''} ${w?.resCat || ''}`.toUpperCase();
  if (!s.trim()) return false;
  if (/PDSI|P\s*&\s*A|P&A|INACTIVE|SHUT|PBP|PUD|LOC|RTP/.test(s) && !/PDP/.test(s)) return false;
  return /PDP|1PDP|PRODUC/.test(s);
}

/** RRC lease rows are the production population when history is lease-level. */
function leaseHistoryRows(ctx) {
  const wells = ctx.wf?.wells || [];
  const leases = wells.filter((w) => w.historyGrain === 'lease');
  if (ctx.wf?.summary?.historyGrain === 'lease' && leases.length) return leases;
  return null;
}

function historyMonths(w) {
  if (Array.isArray(w?.history)) return w.history.length;
  if (Number.isFinite(Number(w?.producingMonths))) return Number(w.producingMonths);
  if (Number.isFinite(Number(w?.historyMonths))) return Number(w.historyMonths);
  return 0;
}

function fileRows(ctx) {
  const facts = (ctx.wf?.facts || []).filter((f) => f.kind === 'wellFileCoverage');
  if (facts.length) {
    return facts.map((f) => ({
      name: f.subject,
      files: Number(f.value?.files) || 0,
      tags: f.value?.tags || [],
      rrc: (f.value?.tags || []).includes('rrc') || f.value?.rrc === 'Y',
      ops: (f.value?.tags || []).includes('ops') || f.value?.ops === 'Y',
    }));
  }
  return (ctx.diligence?.evidenceRoom?.wells || []).map((w) => ({
    name: w.well || w.name,
    files: Number(w.files ?? w.fileCount) || 0,
    tags: w.tags || [],
    rrc: w.rrc === 'Y' || (w.tags || []).includes('rrc'),
    ops: w.ops === 'Y' || (w.tags || []).includes('ops'),
  }));
}

function population(ctx, rows) {
  const master = masterList(ctx).length;
  const wfCount = (ctx.wf?.wells || []).length;
  return Math.max(rows.length, master, wfCount);
}

function vdrHit(ctx, re) {
  return (ctx.diligence?.vdrStatus || []).find((s) => re.test(`${s.section || ''} ${s.note || ''}`));
}

function vdrRows(ctx, re) {
  return (ctx.diligence?.vdrStatus || []).filter((s) => re.test(`${s.section || ''} ${s.note || ''}`));
}

function countiesOf(ctx) {
  const set = new Set();
  for (const c of ctx.pkg?.meta?.counties || []) if (c) set.add(String(c));
  for (const w of [...masterList(ctx), ...(ctx.wf?.wells || []), ...(ctx.pkg?.assets || [])]) {
    if (w?.county) set.add(String(w.county));
  }
  return [...set];
}

function purchasersOf(ctx) {
  return contracts(ctx).filter((c) => /purchaser/i.test(`${c.role || ''} ${c.instrument || ''}`));
}

function hbpSignal(ctx) {
  const k = ctx.land?.kpis || {};
  if (k.hbpPct != null || k.hbpNet != null) return true;
  return acreageUnits(ctx).some((u) => /hbp/i.test(u.unit || u.name || ''));
}

function namedUnits(ctx) {
  return acreageUnits(ctx).filter((u) => {
    const name = u.unit || u.name || '';
    if (!name) return false;
    return !/total|hbp|acreage|expir/i.test(name);
  });
}

function pbpSignal(ctx) {
  const wells = [...(ctx.wf?.wells || []), ...masterList(ctx), ...(ctx.pkg?.assets || [])];
  if (wells.some((w) => /PBP|PUD|2PBP|2RTP|3PUD|3LOC/i.test(`${w.status || ''} ${w.resCat || ''}`))) return true;
  const cases = [...(ctx.pkg?.cases || []), ...(ctx.wf?.cases || [])];
  return cases.some((c) => /PBP|PUD/i.test(`${c.id || ''} ${c.label || ''} ${c.caseLabel || ''} ${c.resCat || ''}`));
}

function capexSignal(ctx) {
  const wells = [...(ctx.wf?.wells || []), ...(ctx.pkg?.assets || [])];
  if (wells.some((w) => Number(w.capexM) > 0 || Number(w.capex) > 0)) return true;
  return pbpSignal(ctx);
}

function lasWells(ctx) {
  return ctx.diligence?.lasFlags?.wells || ctx.geoscience?.lasFlags?.wells || ctx.geoscience?.wells || [];
}

function mapHasShapes(ctx) {
  const layers = ctx.map?.layers || {};
  if (layers.leaseStatus || layers.drillingUnits) return true;
  const note = (ctx.map?.notes || []).join(' ');
  if (/shapefile|geojson/i.test(note)) return true;
  const land = vdrHit(ctx, /shapefile|geojson/i);
  return Boolean(land);
}

function mapHasWells(ctx) {
  if ((ctx.map?.wells || []).some((w) => w.lat != null || w.latitude != null)) return true;
  if ((ctx.wf?.wells || []).some((w) => w.lat != null || w.latitude != null)) return true;
  if ((ctx.map?.kpis?.wellsPlotted || 0) > 0) return true;
  return false;
}

function ariesIngested(ctx) {
  if ((ctx.wf?.provenance || []).some((p) => p.adapter === 'aries-duckdb')) return 'provenance';
  const duck = ctx.pkg?.provenance?.duckdb;
  if (duck && !/^none$/i.test(String(duck))) return 'deal-package';
  if ((ctx.wf?.wells || []).some((w) => w.aries?.forecast?.length)) return 'forecast';
  return null;
}

function prepareDocs(wf) {
  return (wf?.documents || []).map((d) => ({
    ...d,
    docType: resolvedDocType(d) || null,
  }));
}

function overrideFor(overrides, id) {
  const row = (overrides || []).find((o) => (o.itemId || o.item_id) === id);
  if (!row || !STATUSES.includes(row.status)) return null;
  return {
    status: row.status,
    note: row.note || '',
    values: row.values && typeof row.values === 'object' ? row.values : {},
    userName: row.userName || row.user_name || '',
    updatedAt: row.updatedAt || row.updated_at || '',
    catalogVersion: row.catalogVersion || row.catalog_version || '',
  };
}

function formatValue(field, value) {
  if (value == null || value === '') return '';
  if (field?.type === 'currency') {
    const n = Number(value);
    return Number.isFinite(n) ? `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : String(value);
  }
  if (field?.type === 'integer') {
    const n = Number(value);
    return Number.isFinite(n) ? n.toLocaleString('en-US') : String(value);
  }
  return String(value);
}

/** Captured values on upstream rows (catalog `feeds`) show on the rows they feed. */
function upstreamEvidence(item, catalog, overrides) {
  const out = [];
  for (const up of catalog.items || []) {
    if (!(up.feeds || []).includes(item.id)) continue;
    const ov = overrideFor(overrides, up.id);
    if (!ov) continue;
    for (const field of up.fields || []) {
      const text = formatValue(field, ov.values?.[field.key]);
      if (text) out.push(fieldEv(`${field.label}: ${text} (from ${up.id})`, `item:${up.id}`));
    }
  }
  return out;
}

function manualContext(id, ctx) {
  const evidence = [];
  if (id === 'PC-F-01' || id === 'PS-F-01') {
    if (ctx.pkg?.meta?.effectiveDate) evidence.push(fieldEv(`Effective date ${ctx.pkg.meta.effectiveDate}`, 'meta.effectiveDate'));
  }
  if (id === 'PC-F-02') {
    evidence.push(fieldEv('Attest only. Do not store bank details.', 'policy'));
  }
  if (id === 'PC-L-01' && !docsOf(ctx, ['title_opinion']).length) {
    evidence.push(fieldEv('Title opinions (PO-L-03) are not in the data room.', 'PO-L-03'));
  }
  if (id === 'PC-O-04' || id === 'PS-O-01') {
    const n = (ctx.wf?.wells || []).length || masterList(ctx).length || (ctx.pkg?.assets || []).length;
    if (n) evidence.push(fieldEv(`${n} wells on the list`, 'wells'));
  }
  if (id === 'PS-O-02') {
    const names = purchasersOf(ctx).map((c) => c.counterparty).filter(Boolean);
    if (names.length) evidence.push(fieldEv(`Purchasers: ${names.join(', ')}`, 'PO-F-04'));
  }
  if (id === 'PS-L-01') {
    const counties = countiesOf(ctx);
    if (counties.length) evidence.push(fieldEv(`Counties: ${counties.join(', ')}`, 'counties'));
  }
  if (id === 'PO-O-11') {
    const p5 = docsOf(ctx, ['rrc_p5']);
    if (p5.length) evidence.push(...docEv(p5));
  }
  return evidence;
}

const CHECKS = {
  'PO-F-01'(ctx) {
    const months = losMonths(ctx);
    const loe = months.some((m) => m.loe != null);
    const latest = latestYm(months, (m) => m.month || m.period || m.ym);
    const stale = staleNote(ctx, latest, 'LOS');
    const span = latest != null ? ` ending ${ymLabel(latest)}` : '';
    const evidence = [fieldEv(`${months.length} LOS months${span}${loe ? ', LOE present' : ''}`, 'cashflow.losMonths')];
    if (months.length >= 24 && loe && stale) return done('Partial', evidence, stale);
    if (months.length >= 24 && loe) return done('Complete', evidence, 'Two years of LOE.');
    if (months.length >= 24 && !loe) return done('Partial', evidence, '24 months indexed; LOE line not present.');
    if (months.length >= 12) return done('Partial', evidence, 'LOS covers 12–23 months.');
    return done('Missing', evidence, 'Fewer than 12 LOS months.');
  },
  'PO-F-02'(ctx) {
    const byWell = ctx.pkg?.cashflow?.losByWell || ctx.pkg?.losByWell || [];
    const counts = byWell.map((r) => (r.months || r.rows || []).length || Number(r.monthCount) || 0);
    const docs = docsOf(ctx, ['los_by_well']);
    const dealLevel = losMonths(ctx).length;
    const evidence = [
      ...docEv(docs),
      fieldEv(`Deal-level LOS ${dealLevel} months; ${byWell.length} per-well series`, 'cashflow.losMonths'),
    ];
    const latest = latestYm(losMonths(ctx), (m) => m.month || m.period || m.ym);
    const stale = staleNote(ctx, latest, 'LOS');
    if (byWell.length && counts.length && counts.every((c) => c >= 24)) {
      return stale ? done('Partial', evidence, stale) : done('Complete', evidence);
    }
    if (counts.some((c) => c > 0) || docs.length || dealLevel >= 12) return done('Partial', evidence, 'Deal-level or partial well-level LOS only.');
    return done('Missing', evidence);
  },
  'PO-F-04'(ctx) {
    const buyers = purchasersOf(ctx);
    const docs = docsOf(ctx, ['purchase_contract']);
    const evidence = [
      ...buyers.map((c) => fieldEv(c.counterparty || c.role, `contract:${c.id || c.counterparty}`)),
      ...docEv(docs),
    ];
    if (buyers.length || docs.length) return done('Complete', evidence);
    return done('Missing', evidence);
  },
  'PO-F-08'(ctx) {
    const relevant = contracts(ctx).filter((c) => /purchas|gather|transport|marketing/i.test(`${c.role || ''} ${c.instrument || ''}`));
    const docs = docsOf(ctx, ['purchase_contract', 'gathering', 'transport']);
    const open = relevant.some((c) => /open/i.test(c.status || ''));
    const evidence = [
      ...relevant.map((c) => fieldEv(`${c.counterparty || c.id}: ${c.status || 'unknown'}`, `contract:${c.id || ''}`)),
      ...docEv(docs),
    ];
    if (!relevant.length && !docs.length) return done('Missing', evidence);
    if (open || !docs.length) return done('Partial', evidence, open ? 'Assignability still open on at least one contract.' : 'Contracts named; instruments not indexed.');
    return done('Complete', evidence);
  },
  'PO-O-01'(ctx) {
    const rows = fileRows(ctx);
    const tagged = rows.filter((r) => r.rrc).length;
    const total = population(ctx, rows);
    const docs = docsOf(ctx, ['rrc_p4', 'rrc_p5']);
    const evidence = [fieldEv(`${tagged} of ${total || rows.length} indexed wells tagged RRC`, 'well-files'), ...docEv(docs)];
    if (total && tagged >= total) return done('Complete', evidence);
    if (tagged > 0 || docs.length) return done('Partial', evidence, 'RRC coverage is a subset of wells.');
    return done('Missing', evidence);
  },
  'PO-O-02'(ctx) {
    const leaseHist = leaseHistoryRows(ctx);
    const universe = leaseHist
      || ((ctx.wf?.wells || []).some((w) => Array.isArray(w.history))
        ? ctx.wf.wells
        : masterList(ctx));
    const pdp = leaseHist || universe.filter(isPdp);
    const good = pdp.filter((w) => historyMonths(w) >= 24).length;
    const latest = latestYm(
      pdp.flatMap((w) => (Array.isArray(w.history) ? w.history.slice(-1) : [])),
      historyDate,
    );
    const stale = staleNote(ctx, latest, 'Production history');
    const docs = docsOf(ctx, ['production_history']);
    const noun = leaseHist ? 'RRC leases' : 'PDP wells';
    const evidence = [
      fieldEv(`${good} of ${pdp.length} ${noun} with ≥24 months${latest != null ? ` through ${ymLabel(latest)}` : ''}`, 'wells.history'),
      ...docEv(docs),
    ];
    if (pdp.length && good === pdp.length && stale) return done('Partial', evidence, stale);
    if (pdp.length && good === pdp.length) return done('Complete', evidence);
    if (good > 0) return done('Partial', evidence);
    if (docs.length) return done('Partial', evidence, 'Production history is indexed; per-well 24-month coverage is not confirmed.');
    const forecast = ariesIngested(ctx);
    if (forecast && /AC_PRODUCT/i.test(ctx.pkg?.meta?.streamMethod || '')) {
      return done('Partial', evidence, 'Production history is referenced; per-well 24-month coverage is not confirmed.');
    }
    return done('Missing', evidence);
  },
  'PO-O-04'(ctx) {
    const rows = fileRows(ctx);
    const ops = rows.filter((r) => r.ops).length;
    const docs = docsOf(ctx, ['workover']);
    const evidence = [fieldEv(`${ops} of ${rows.length} wells tagged ops`, 'well-files'), ...docEv(docs)];
    if (!ops && !docs.length) return done('Missing', evidence);
    if (docs.length && rows.length && ops === rows.length) return done('Complete', evidence);
    return done('Partial', evidence, 'Workover history covers a subset of wells.');
  },
  'PO-O-08'(ctx) {
    const list = masterList(ctx).length ? masterList(ctx) : (ctx.wf?.wells || []).length ? ctx.wf.wells : ctx.pkg?.assets || [];
    const counts = statusCounts(ctx);
    const withStatus = list.filter((w) => w.status || w.resCat);
    const evidence = [fieldEv(`${list.length} wells${counts ? `, status counts present` : ''}`, 'masterWells')];
    if (list.length && counts && withStatus.length) return done('Complete', evidence);
    if (list.length) return done('Partial', evidence, 'Well list present; status counts incomplete.');
    return done('Missing', evidence);
  },
  'PO-O-09'(ctx) {
    const docs = docsOf(ctx, ['equipment_inventory']);
    const leased = contracts(ctx).filter((c) => /compressor/i.test(`${c.instrument || ''} ${c.role || ''}`));
    const evidence = [...docEv(docs), ...leased.map((c) => fieldEv(c.instrument || c.counterparty, `contract:${c.id || ''}`))];
    if (docs.length && !leased.length) return done('Complete', evidence);
    if (docs.length || leased.length) return done('Partial', evidence, leased.length ? 'Leased equipment is on the register; no tank inventory.' : '');
    return done('Missing', evidence);
  },
  'PO-O-10'(ctx) {
    const docs = docsOf(ctx, ['swd']);
    const wells = [...masterList(ctx), ...(ctx.wf?.wells || []), ...(ctx.pkg?.assets || [])];
    const swd = wells.filter((w) => /SWD|DISPOSAL/i.test(`${w.wellType || ''} ${w.status || ''} ${w.type || ''}`));
    const evidence = [...docEv(docs), ...swd.slice(0, 8).map((w) => fieldEv(w.lease || w.name || 'SWD', 'wellType'))];
    if (docs.length || swd.length) return done('Complete', evidence);
    return done('Missing', evidence, 'No disposal well on the list and no disposal agreement in the room. Attest if disposal is trucked.');
  },
  'PO-O-12'(ctx) {
    const rows = lasWells(ctx);
    const withLas = rows.filter((w) => Number(w.lasCount) > 0 || w.hasLas);
    const docs = docsOf(ctx, ['well_log', 'drilling_prognosis']);
    const fileCount = ctx.diligence?.lasFlags?.fileCount || ctx.geoscience?.fileCount || ctx.diligence?.logEvidence?.fileCount || 0;
    const known = masterList(ctx).length || (ctx.wf?.wells || []).length || rows.length;
    const evidence = [fieldEv(`${withLas.length} wells with LAS${fileCount ? ` · ${fileCount} files` : ''}`, 'lasFlags'), ...docEv(docs)];
    if (!withLas.length && !fileCount && !docs.length) return done('Missing', evidence);
    if (known && withLas.length >= known) return done('Complete', evidence);
    if (!withLas.length && !fileCount && docs.length) return done('Partial', evidence, 'Well logs are indexed; LAS coverage is not.');
    return done('Partial', evidence, 'G&G coverage is a subset of wells.');
  },
  'PO-O-13'(ctx) {
    const counts = statusCounts(ctx);
    const list = masterList(ctx).length ? masterList(ctx) : ctx.wf?.wells || [];
    const known = Boolean(counts) || list.some((w) => w.status || w.resCat);
    const docs = docsOf(ctx, ['rrc_14b2', 'plugging_extension']);
    const evidence = [fieldEv(known ? 'Active and inactive status is on the well list' : 'No well status', 'statusCounts'), ...docEv(docs)];
    if (known && docs.length) return done('Complete', evidence);
    if (known) return done('Partial', evidence, 'Status is known; plugging deadlines and extensions are not.');
    return done('Missing', evidence);
  },
  'PO-O-14'(ctx) {
    const docs = docsOf(ctx, ['afe', 'capex']);
    const evidence = [fieldEv(capexSignal(ctx) ? 'Capex or a PBP/PUD case is in the model' : 'No capex case', 'capex'), ...docEv(docs)];
    if (capexSignal(ctx) && docs.length) return done('Complete', evidence);
    if (capexSignal(ctx) || docs.length) return done('Partial', evidence, 'Capex is in the model; AFE results are not indexed.');
    return done('Missing', evidence);
  },
  'PO-O-15'(ctx) {
    const rows = fileRows(ctx);
    const withFiles = rows.filter((r) => r.files > 0).length;
    const total = population(ctx, rows);
    const room = ctx.diligence?.evidenceRoom;
    const opsDocs = docsOf(ctx, ['daily_drilling_report', 'flowback_report']);
    const diagrams = docsOf(ctx, ['wellbore_diagram']);
    const evidence = [
      fieldEv(`${withFiles} of ${total} wells with files${room?.fileCount ? ` · ${room.fileCount} files` : ''}`, 'well-file-index'),
      ...docEv(opsDocs),
      ...docEv(diagrams),
    ];
    if (!withFiles && !(room?.fileCount > 0) && !diagrams.length) return done('Missing', evidence);
    if (!withFiles && !(room?.fileCount > 0) && diagrams.length) {
      return done('Partial', evidence, 'Wellbore diagrams are indexed; a well-file grade is not.');
    }
    if (total && withFiles >= total) return done('Complete', evidence);
    return done('Partial', evidence, 'Well-file coverage is a subset.');
  },
  'PO-L-02'(ctx) {
    const docs = docsOf(ctx, ['lease_term_schedule']);
    const units = acreageUnits(ctx);
    const evidence = [fieldEv(hbpSignal(ctx) ? 'HBP / acreage rollup is loaded' : 'No acreage rollup', 'acreageUnits'), ...docEv(docs)];
    if (docs.length && (hbpSignal(ctx) || units.length)) return done('Complete', evidence);
    if (hbpSignal(ctx) || units.length || mapHasShapes(ctx)) return done('Partial', evidence, 'HBP rollup is in; primary term, depth, and shut-in royalty are not scheduled.');
    return done('Missing', evidence);
  },
  'PO-L-04'(ctx) {
    const leases = docsOf(ctx, ['lease', 'amendment', 'ratification', 'extension']);
    const deducts = losMonths(ctx).some((m) => m.gasDeducts != null || m.deducts != null);
    const evidence = [...docEv(leases)];
    if (deducts) evidence.push(fieldEv('LOS shows gas deducts; clauses are not a substitute for the lease', 'cashflow.losMonths'));
    if (!leases.length) return done('Missing', evidence, 'Royalty deduction clauses have not been reviewed.');
    return done('Partial', evidence, 'Lease instruments are indexed; post-production clause review still needs an attestation.');
  },
  'PO-L-06'(ctx) {
    const cases = [...(ctx.pkg?.cases || []), ...(ctx.wf?.cases || [])];
    const seller = cases.filter((c) => /sellerreserve|reserve report|^aries$|aries/i.test(String(c.caseSource || c.source || '')));
    const docs = docsOf(ctx, ['reserve_report']);
    const vdr = vdrHit(ctx, /reserve report/i);
    const vdrLive = vdr && /live/i.test(vdr.status || '');
    const evidence = [
      ...seller.map((c) => fieldEv(c.caseLabel || c.id || c.caseSource, 'cases')),
      ...docEv(docs),
    ];
    if (vdrLive) evidence.push(fieldEv(`${vdr.section}: ${vdr.note || vdr.status}`, 'vdrStatus'));
    if (seller.length || docs.length || vdrLive) return done('Complete', evidence);
    if ((ctx.pkg?.assets || []).some((a) => a.ref?.pv10 != null)) return done('Partial', evidence, 'Seller PV is on the wells; no reserve-report case.');
    return done('Missing', evidence);
  },
  'PO-L-07'(ctx) {
    const via = ariesIngested(ctx);
    const docs = docsOf(ctx, ['seller_forecast']);
    const rows = vdrRows(ctx, /phd\s*win|aries database|seller forecast|decline curve/i);
    const vdrLive = rows.find((s) => /live/i.test(s.status || ''));
    const vdrPartial = rows.find((s) => /partial/i.test(s.status || ''));
    const evidence = [
      ...(via ? [fieldEv(`Seller forecast case (${via})`, 'provenance')] : []),
      ...docEv(docs),
    ];
    if (vdrLive) evidence.push(fieldEv(`${vdrLive.section}: ${vdrLive.note || vdrLive.status}`, 'vdrStatus'));
    if (via || docs.length || vdrLive) return done('Complete', evidence, 'Seller forecast case.');
    if (vdrPartial || /AC_ECONOMIC|aries|phd\s*win/i.test(ctx.pkg?.meta?.streamMethod || '')) {
      return done('Partial', evidence, 'A seller forecast is mentioned; the case is not on file.');
    }
    return done('Missing', evidence, 'No seller forecast case.');
  },
  'PO-L-08'(ctx) {
    const docs = docsOf(ctx, ['drilling_plan']);
    const planned = pbpSignal(ctx);
    const evidence = [fieldEv(planned ? 'PUD/PBP case is in the model' : 'No undeveloped case', 'cases'), ...docEv(docs)];
    if (docs.length && planned) return done('Complete', evidence);
    if (docs.length || planned) return done('Partial', evidence, 'A development case is in the model; permitted and unpermitted plans are not both in the room.');
    return done('Missing', evidence);
  },
  'PO-L-10'(ctx) {
    const docs = docsOf(ctx, ['ppr', 'consent']);
    const open = contracts(ctx).filter((c) => /open/i.test(c.status || ''));
    const overall = /open/i.test(ctx.diligence?.midstreamAssignability?.overallStatus || '');
    const evidence = [...docEv(docs), ...open.map((c) => fieldEv(`${c.id || c.counterparty} open`, `contract:${c.id || ''}`))];
    if (docs.length && !open.length && !overall) return done('Complete', evidence);
    if (docs.length || open.length || overall) return done('Partial', evidence, 'Assignability is open; lease PPR and consents are not fully reviewed.');
    return done('Missing', evidence);
  },
  'PO-L-12'(ctx) {
    const modeled = modeledWells(ctx);
    const ok = modeled.filter(hasInterest);
    const evidence = [fieldEv(`${ok.length} of ${modeled.length} modeled wells have WI and NRI`, 'wells.wi')];
    if (modeled.length && ok.length === modeled.length) return done('Complete', evidence);
    if (ok.length) return done('Partial', evidence);
    return done('Missing', evidence);
  },
  'PO-L-13'(ctx) {
    const docs = docsOf(ctx, ['burden_schedule']);
    const modeled = modeledWells(ctx);
    const ok = modeled.filter(hasInterest);
    const evidence = [fieldEv(ok.length ? 'Implied burden is WI − NRI' : 'No WI/NRI', 'wells.wi'), ...docEv(docs)];
    if (docs.length) return done('Complete', evidence);
    if (ok.length) return done('Partial', evidence, 'Aggregate burden is derivable; royalties, ORRI, NPI, and production payments are not itemized.');
    return done('Missing', evidence);
  },
  'PO-L-14'(ctx) {
    const wells = mapHasWells(ctx);
    const shapes = mapHasShapes(ctx);
    const plats = docsOf(ctx, ['plat']);
    const evidence = [fieldEv(`${wells ? 'Well locations' : 'No well locations'} · ${shapes ? 'land shapes' : 'no land shapes'}`, 'map'), ...docEv(plats)];
    if (wells && shapes) return done('Complete', evidence);
    if (wells || shapes) return done('Partial', evidence);
    if (plats.length) return done('Partial', evidence, 'Plats are indexed; the map is not loaded.');
    return done('Missing', evidence);
  },
  'PO-L-15'(ctx) {
    const docs = docsOf(ctx, ['unit', 'pooling', 'farmout', 'ami']);
    const units = namedUnits(ctx);
    const evidence = [...units.slice(0, 6).map((u) => fieldEv(u.unit || u.name, 'acreageUnits')), ...docEv(docs)];
    if (docs.length) return done('Complete', evidence);
    if (units.length) return done('Partial', evidence, 'Unit names are on the acreage rollup; the agreements are not indexed.');
    return done('Missing', evidence);
  },
  'PC-F-04'(ctx) {
    const suspense = docsOf(ctx, ['suspense_report']);
    const escheat = docsOf(ctx, ['escheat']);
    const evidence = [...docEv(suspense), ...docEv(escheat)];
    if (suspense.length && escheat.length) return done('Complete', evidence);
    if (suspense.length || escheat.length) return done('Partial', evidence, 'Suspense and escheat are not both in the room.');
    return done('Missing', evidence);
  },
  'PC-L-02'(ctx) {
    const docs = docsOf(ctx, ['psa']);
    if (!docs.length) return done('Missing', [], 'PSA is not in the data room. Copy count is an attestation.');
    return done('Partial', docEv(docs), 'PSA is indexed. Attest that three copies are in hand.');
  },
  'PC-L-03'(ctx) {
    const docs = docsOf(ctx, ['assignment_bos']);
    const need = countiesOf(ctx);
    const covered = need.filter((c) => docs.some((d) => `${d.title || ''} ${d.folder || ''} ${d.path || ''}`.toLowerCase().includes(c.toLowerCase())));
    const evidence = [fieldEv(need.length ? `Counties: ${need.join(', ')}` : 'No county list', 'counties'), ...docEv(docs)];
    if (!docs.length) return done('Missing', evidence);
    if (need.length && covered.length === need.length) return done('Partial', evidence, 'An assignment is indexed for each county. Attest the three copies.');
    if (need.length) return done('Partial', evidence, `${covered.length} of ${need.length} counties have an assignment indexed.`);
    return done('Partial', evidence);
  },
};

function evaluateItem(item, ctx) {
  if (item.kind === 'disclosure') {
    // Evidence found is a risk to review, never a green check.
    const found = docsOf(ctx, item.rule?.docTypes || []);
    if (found.length) return done('Partial', docEv(found), `${found.length} document${found.length === 1 ? '' : 's'} in the room need review. Attest once reviewed.`);
    return done('Missing', manualContext(item.id, ctx), 'Nothing disclosed yet. Attest "none known" or list the issues.');
  }
  if (CHECKS[item.id]) return CHECKS[item.id](ctx);
  if (item.method === 'manual') {
    return done('Missing', manualContext(item.id, ctx), item.kind === 'signoff' ? 'Needs a named sign-off.' : 'Needs an attestation.');
  }
  const found = docsOf(ctx, item.rule?.docTypes || []);
  if (found.length) return done('Complete', docEv(found));
  return done('Missing', [], 'Not in the data room.');
}

function resolveDeal(input) {
  const deal = input.deal || {};
  const meta = input.pkg?.meta || {};
  const buyer = { ...buyerDefault, ...(input.buyer || deal.buyerProfile || {}) };
  const settings = input.settings || {};
  return {
    states: deal.states || (meta.state ? [meta.state] : []),
    operated: deal.operated == null ? (meta.operated == null ? null : Boolean(meta.operated)) : Boolean(deal.operated),
    buyer,
    dates: {
      today: isoDay(input.today) || new Date().toISOString().slice(0, 10),
      effectiveDate: isoDay(settings.effectiveDate || meta.effectiveDate || deal.effectiveDate),
      closingDate: isoDay(settings.closingDate || deal.closingDate || meta.closingDate),
    },
  };
}

/**
 * @param {{ pkg?: object, diligence?: object, wf?: object, overrides?: object[], wells?: object, land?: object, map?: object, geoscience?: object, stage?: string, catalog?: object, deal?: object, buyer?: object, settings?: object, today?: string }} input
 */
export function computeChecklist(input = {}) {
  const catalog = input.catalog || CATALOG;
  const stage = resolveStage(input.overrides, input.stage || input.settings?.stage);
  const resolved = resolveDeal(input);
  const ctx = {
    pkg: input.pkg || null,
    diligence: input.diligence || null,
    wf: input.wf || null,
    wells: input.wells || null,
    land: input.land || null,
    map: input.map || null,
    geoscience: input.geoscience || null,
    docs: prepareDocs(input.wf),
    dates: resolved.dates,
  };
  const items = (catalog.items || []).map((item) => {
    const reached = phaseReached(item.phase, stage);
    const { applicable, reason } = applicability(item, resolved);
    const dueDate = dueDateFor(item, resolved.dates);
    let auto;
    if (!applicable) auto = done('N/A', [], reason);
    else if (!reached) auto = done('Upcoming', [], `Opens at ${PHASE_LABEL[item.phase]}.${dueDate ? ` Due ${dueDate}.` : ''}`);
    else auto = evaluateItem(item, ctx);
    const ov = overrideFor(input.overrides, item.id);
    const status = ov ? ov.status : auto.status;
    const open = status !== 'Complete' && status !== 'N/A' && status !== 'Upcoming';
    const overdue = Boolean(item.due && open && dueDate && dueDate < resolved.dates.today);
    const evidence = [...(auto.evidence || []), ...upstreamEvidence(item, catalog, input.overrides)];
    return {
      id: item.id,
      phase: item.phase,
      category: item.category,
      item: interpolate(labelFor(item, resolved), resolved.buyer),
      whatSatisfies: interpolate(item.whatSatisfies || '', resolved.buyer),
      kind: item.kind || 'request',
      owner: item.owner || '',
      method: item.method,
      requiredForPhase: Boolean(item.requiredForPhase),
      fields: item.fields || [],
      feeds: item.feeds || [],
      rule: item.rule?.text || '',
      status,
      autoStatus: auto.status,
      source: ov ? 'override' : 'auto',
      evidence,
      detail: ov?.note || auto.detail || '',
      override: ov,
      dueDate,
      overdue,
      applicable,
      reached,
    };
  });
  const { counts, readiness, byCategory, byOwner } = summarize(items);
  const missingRequired = items.filter((i) => i.phase === 'pre_offer' && i.requiredForPhase && i.status === 'Missing').length;
  const overdue = items.filter((i) => i.overdue).length;
  return {
    catalogVersion: catalog.version,
    stage,
    deal: { states: resolved.states, operated: resolved.operated, buyer: resolved.buyer },
    dates: resolved.dates,
    items,
    counts,
    readiness,
    byCategory,
    byOwner,
    gaps: items.filter((i) => i.status === 'Missing' || i.status === 'Partial'),
    warnings: { preOfferMissingRequired: missingRequired, overdue },
  };
}

function tally(list) {
  const t = { Complete: 0, Partial: 0, Missing: 0, 'N/A': 0, Upcoming: 0 };
  for (const item of list) t[item.status] = (t[item.status] || 0) + 1;
  return t;
}

/** Complete and partial counts on the required set. Not the half-weighted score. */
export function readinessCountLabel(score) {
  if (!score || score.required == null || score.complete == null) return null;
  return `${score.complete} complete, ${score.partial ?? 0} partial of ${score.required} required`;
}

/** "27% · 5 complete, 4 partial of 26 required". */
export function readinessHeadline(score) {
  if (!score || score.pct == null) return null;
  const detail = readinessCountLabel(score);
  return detail ? `${score.pct}% · ${detail}` : `${score.pct}%`;
}

function score(list) {
  const req = list.filter((i) => i.requiredForPhase && i.status !== 'N/A' && i.status !== 'Upcoming');
  const complete = req.filter((i) => i.status === 'Complete').length;
  const partial = req.filter((i) => i.status === 'Partial').length;
  const missing = req.filter((i) => i.status === 'Missing').length;
  const s = complete + partial * 0.5;
  return {
    pct: req.length ? Math.round((s / req.length) * 100) : null,
    required: req.length,
    complete,
    partial,
    missing,
    score: s,
    overdue: list.filter((i) => i.overdue).length,
  };
}

function summarize(items) {
  const counts = {};
  const readiness = {};
  const byCategory = {};
  const byOwner = {};
  for (const phase of Object.keys(PHASE_ORDER)) {
    const inPhase = items.filter((i) => i.phase === phase);
    counts[phase] = tally(inPhase);
    readiness[phase] = score(inPhase);
    byCategory[phase] = {};
    for (const cat of Object.keys(CATEGORY_LABEL)) {
      byCategory[phase][cat] = score(inPhase.filter((i) => i.category === cat));
    }
  }
  for (const owner of OWNERS) {
    byOwner[owner] = score(items.filter((i) => i.owner === owner));
  }
  return { counts, readiness, byCategory, byOwner };
}

/** Page context for the assistant: ids and labels, no document bodies. */
export function checklistForAssistant(result) {
  if (!result?.items?.length) return null;
  const pick = (status) =>
    result.items
      .filter((i) => i.status === status)
      .map((i) => ({ id: i.id, item: i.item, phase: i.phase, owner: i.owner, required: i.requiredForPhase }));
  return {
    catalogVersion: result.catalogVersion,
    stage: result.stage,
    readinessPct: result.readiness?.pre_offer?.pct ?? null,
    // Required-item tally, so complete / partial / missing reproduce readinessPct.
    counts: result.readiness?.pre_offer
      ? {
          Complete: result.readiness.pre_offer.complete,
          Partial: result.readiness.pre_offer.partial,
          Missing: result.readiness.pre_offer.missing,
          required: result.readiness.pre_offer.required,
          score: result.readiness.pre_offer.score,
        }
      : null,
    requiredItemsLabel: readinessCountLabel(result.readiness?.pre_offer),
    allItemCounts: result.counts?.pre_offer || null,
    byCategory: result.byCategory?.pre_offer || null,
    dates: result.dates || null,
    missing: pick('Missing'),
    partial: pick('Partial'),
    overdue: result.items.filter((i) => i.overdue).map((i) => ({ id: i.id, item: i.item, owner: i.owner, dueDate: i.dueDate })),
  };
}
